import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogCloseEvent.eventName, this.#clearRowErrors);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogCloseEvent.eventName, this.#clearRowErrors);
  }

  #handleCartAdd = (/** @type {Event} */ event) => {
    if (!this.hasAttribute('auto-open')) return;
    // Narrow auto-open to successful PDP adds only. Without these filters, the
    // drawer re-opens after the user deliberately closed it mid-mutation —
    // e.g. close during in-flight qty +/− or remove (source === 'cart-items-component'),
    // or after an ATC error (didError: true) that shows its feedback on the PDP
    // rather than in the drawer.
    const data = /** @type {any} */ (event).detail?.data;
    if (!data) return;
    if (data.didError) return;
    if (data.source !== 'product-form-component') return;
    this.showDialog();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  // Clear any per-row error messages on close so they don't persist when the
  // user reopens the drawer. Fires on every close path (button, Esc, backdrop).
  #clearRowErrors = () => {
    this.querySelectorAll('.cart-items__error:not(.hidden)').forEach((el) => {
      el.classList.add('hidden');
      const text = el.querySelector('.cart-item__error-text');
      if (text) text.textContent = '';
    });
  };

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
